import { useMemo } from "react";
import { Alert, ListGroup } from "react-bootstrap";
import { Link } from "react-router-dom";
import SubmitButton from "../../../components/SubmitButton";
import {
  SlantLinkHeldAlert,
  slantLinksHeld,
} from "../../contact/SlantLinkHeld";
import {
  SlantPerson,
  SwitchDecision,
  householdAlreadyMapped,
  stepErrorMessage,
  useSwitchToSlant,
} from "./api";
import type { ReviewRow } from "./SwitchToSlantStep3";

/**
 * Step 4 — Review & switch.
 *
 * Shows the confirmed match list one last time, then calls the single `switch`
 * mutation — the atomic Wealthbox → Slant re-point. On success it renders a
 * summary ("N people linked, household re-pointed to Slant"); on failure it
 * surfaces the error and offers a retry. STATELESS: a retry just re-POSTs the
 * same body — there is no server-side row to resume.
 *
 * One failure is shown differently (APP-1690). When the api refuses the switch
 * because the chosen Slant household is already linked to a different
 * AdviceCloud household (`householdAlreadyMapped`), the alert reads "Household
 * already mapped" and links to that household's page. That refusal has no
 * retry button: a retry sends the same body and gets the same 409.
 *
 * A second refusal is shown differently too (APP-1706). When a different
 * contact holds a Slant person of the switch, the alert links to the page of
 * that contact and tells the advisor to remove its Slant link on the Contact
 * Data Connections page. The retry button stays: after that removal, the same
 * body succeeds.
 */
const SwitchToSlantStep4 = ({
  householdId,
  slantHouseholdId,
  rows,
  decisions,
  slantPeople,
}: {
  householdId: number;
  slantHouseholdId: string;
  rows: ReviewRow[];
  decisions: SwitchDecision[];
  slantPeople: SlantPerson[];
}) => {
  const doSwitch = useSwitchToSlant(householdId);

  const slantNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const person of slantPeople) {
      map.set(person.id, person.name);
    }
    return map;
  }, [slantPeople]);

  const decisionByPerson = useMemo(() => {
    const map = new Map<number, string>();
    for (const decision of decisions) {
      map.set(decision.contact_id, decision.slant_person_id);
    }
    return map;
  }, [decisions]);

  const result = doSwitch.data?.data ?? null;

  const onSwitch = () => doSwitch.mutate({ slantHouseholdId, decisions });

  const alreadyMapped = doSwitch.isError
    ? householdAlreadyMapped(doSwitch.error)
    : null;
  // A Slant person that a different contact holds (APP-1706,
  // doc-held-slant-contact-links-01a11cd9686f430c, R4, R5).
  const held = doSwitch.isError ? slantLinksHeld(doSwitch.error) : null;

  if (doSwitch.isSuccess && result !== null) {
    const linked = result.people;
    return (
      <div>
        <Alert variant="success">
          <Alert.Heading>Switched to Slant</Alert.Heading>
          <p className="mb-0">
            {linked} {linked === 1 ? "person" : "people"} linked, household
            re-pointed to Slant.
          </p>
        </Alert>
        <ListGroup className="mb-0">
          <ListGroup.Item className="d-flex justify-content-between">
            <span>People linked</span>
            <span>{result.people}</span>
          </ListGroup.Item>
          <ListGroup.Item className="d-flex justify-content-between">
            <span>Household re-pointed</span>
            <span>{result.household_repointed ? "Yes" : "No"}</span>
          </ListGroup.Item>
        </ListGroup>
      </div>
    );
  }

  return (
    <div>
      <h5>Review &amp; switch</h5>
      <p className="text-muted">
        Confirm the matches below. Switching links these people and re-points
        the household to Slant in a single step — this cannot be undone here.
      </p>

      {rows.length === 0 ? (
        <Alert variant="warning">No matched people to switch.</Alert>
      ) : (
        <ListGroup className="mb-3">
          {rows.map((row) => {
            const chosenId = decisionByPerson.get(row.contactId);
            const chosenName =
              (chosenId ? slantNameById.get(chosenId) : undefined) ?? chosenId;
            return (
              <ListGroup.Item
                key={row.contactId}
                className="d-flex justify-content-between"
              >
                <span>{row.name}</span>
                <small className="text-muted">→ {chosenName}</small>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      )}

      {alreadyMapped !== null && (
        <Alert variant="danger">
          <div>Household already mapped</div>
          {alreadyMapped.household !== null && (
            // An anchor, not a button: the Overview tab renders this wizard
            // inside a `<Form>`, and a click on an anchor does not submit it.
            <Alert.Link
              as={Link}
              to={`/clients/households/${alreadyMapped.household.id}`}
            >
              {alreadyMapped.household.name ?? "Open the other household"}
            </Alert.Link>
          )}
        </Alert>
      )}

      {held !== null && (
        <SlantLinkHeldAlert
          held={held}
          slantNameOf={(slantContactId) => slantNameById.get(slantContactId)}
        />
      )}

      {doSwitch.isError && alreadyMapped === null && held === null && (
        <Alert variant="danger">
          {stepErrorMessage(
            doSwitch.error,
            "The switch failed. Please try again.",
          )}
        </Alert>
      )}

      {alreadyMapped === null && (
        <div className="d-flex justify-content-end">
          <SubmitButton
            label={
              doSwitch.isError ? "Retry switch" : "Confirm — switch to Slant"
            }
            variant={doSwitch.isError ? "secondary" : "primary"}
            onClick={onSwitch}
            isSubmitting={doSwitch.isPending}
            disabled={rows.length === 0}
          />
        </div>
      )}
    </div>
  );
};

export default SwitchToSlantStep4;
